import { useEffect, useRef, useState } from 'preact/hooks';
import { normalizePath, resolveRoute, notFound } from './routes';

const navigation = [['/', 'Home'], ['/c/bio','Bio'], ['/c/works','Works'], ['/c/exhibitions','Exhibitions'], ['/c/contacts','Contacts']];
export function App({initialPath = '/'}) {
  const [path, setPath] = useState(normalizePath(initialPath));
  const [viewer, setViewer] = useState(null);
  const heading = useRef(null);
  const route = resolveRoute(path);
  const Page = route.Component;
  useEffect(()=>{
    const pop = ()=> {
      document.title = resolveRoute(location.pathname).title;
      setViewer(null);
      setPath(normalizePath(location.pathname));
    };
    // Update the title before Analytics observes back/forward navigation.
    window.addEventListener('popstate',pop,true);
    return ()=>window.removeEventListener('popstate',pop,true);
  },[]);
  useEffect(()=>{
    document.title = route.title;
    document.querySelector('meta[name="description"]')?.setAttribute('content',route.description);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      const previous = canonical.getAttribute('href');
      canonical.setAttribute('href', /^https?:/.test(previous) ? new URL(route.canonical, previous).href : route.canonical);
    }
    document.body.id = route.section;
  },[route]);
  function follow(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a');
    if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href,location.href);
    if (url.origin !== location.origin) return;
    if (link.dataset.lightbox) {
      event.preventDefault();
      const links = [...heading.current.querySelectorAll('a[data-lightbox]')].filter(a=>a.dataset.lightbox === link.dataset.lightbox);
      setViewer({photos:links.map(a=>({src:a.href,alt:a.querySelector('img')?.alt || route.title})),index:links.indexOf(link),opener:link});
      return;
    }
    if (url.hash || resolveRoute(url.pathname) === notFound) return;
    event.preventDefault();
    // GA4 enhanced measurement observes History API changes. Set the new
    // title first so the virtual page view has the destination's title.
    document.title = resolveRoute(url.pathname).title;
    history.pushState(null,'',url.pathname+url.search);
    setViewer(null);
    setPath(normalizePath(url.pathname));
    window.scrollTo(0,0);
    heading.current?.focus({preventScroll:true});
  }
  return <div onClick={follow}>
    <a class="skip-link" href="#content-section">Skip to content</a>
    <div id="container">
      <header id="mainHeader"><div class="headWrp">
        <a id="logo" href="/" title="Paddy Campbell — Sculptor">Paddy Campbell</a>
        <nav aria-label="Main navigation"><ul>{navigation.map(([href,label])=>{
          const selected = route.canonical === href || (href === '/c/works' && route.section === 'works');
          return <li key={href}><a href={href} class={selected ? 'sel' : undefined} aria-current={selected ? 'page' : undefined}>{label}</a></li>;
        })}</ul></nav>
      </div></header>
      <main id="content"><section id="content-section" class={['gallery','sculpture','milestones','expositions','commissions','scenarios','exhibition'].includes(route.section) ? 'editorial' : ''} ref={heading} tabIndex="-1">
        <Page key={route.canonical} {...route.props}/>
      </section></main>
    </div>
    <footer id="foot"><div class="footerContent">
      <p><a href="https://www.instagram.com/paddycampbell_sculptor/" target="_blank" rel="noopener noreferrer" class="instagranLink"><img src="/icons/instagram.svg" alt="" class="iconBook"/> Instagram</a></p>
      <p>© Paddy Campbell 2026</p>
      <p><a href="https://www.digital-design.it" target="_blank" rel="noopener noreferrer">Made in Digital Design</a></p>
    </div></footer>
    {viewer && <ImageViewer viewer={viewer} close={()=>setViewer(null)} change={index=>setViewer({...viewer,index:(index+viewer.photos.length)%viewer.photos.length})}/>}
  </div>;
}
function ImageViewer({viewer,close,change}) {
  const dialog = useRef(null);
  const {photos,index,opener} = viewer;
  useEffect(()=>{
    dialog.current.showModal();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return ()=> { document.body.style.overflow = overflow; opener?.focus(); };
  },[]);
  return <dialog ref={dialog} class="image-viewer" aria-label="Artwork image viewer" onCancel={event=>{event.preventDefault();close();}} onClick={event=>{if(event.target === dialog.current)close();}} onKeyDown={event=>{
    if(event.key === 'Escape'){event.preventDefault();close();}
    if(event.key === 'ArrowLeft'){event.preventDefault();change(index-1);}
    if(event.key === 'ArrowRight'){event.preventDefault();change(index+1);}
  }}>
    <div class="viewer-controls"><button type="button" onClick={close} autoFocus aria-label="Close image viewer">Close ×</button></div>
    <img src={photos[index].src} alt={photos[index].alt}/>
    <div class="viewer-controls">{photos.length > 1 && <><button type="button" onClick={()=>change(index-1)} aria-label="Previous image">← Previous</button><span aria-live="polite">{index+1} / {photos.length}</span><button type="button" onClick={()=>change(index+1)} aria-label="Next image">Next →</button></>}</div>
  </dialog>;
}
